import type { AxiosInstance, AxiosResponse } from 'axios';
import type { Nullable } from '@gov-nx/core/types/helpers.types';
import type { OsobyKomunikacniUdajeHistorieSeznamDto } from '../generated/osoby.types';
import type {
	FirebaseTokenPayload,
	NastaveniSeznamApiActualDto,
	UserSetting,
} from '../types/userSettings';

export const saveSettingsQuery = <Value>(
	axiosInstance: AxiosInstance,
	payload: {
		klic: string;
		hodnota: Value;
	}
): Promise<AxiosResponse<void, void>> =>
	axiosInstance.post('/api/v1/be/nastaveni', payload, {
		headers: {
			'Content-Type': 'application/json',
		},
	});

export const fetchSettingsQuery = async <Value>(
	axiosInstance: AxiosInstance,
	key: string
): Promise<{
	klic: string;
	hodnota?: Value;
}> => {
	const { data } = await axiosInstance.get(`/api/v1/be/nastaveni/${key}`);
	return data;
};

export const fetchSettingsHistoryQuery = async (
	axiosInstance: AxiosInstance
): Promise<OsobyKomunikacniUdajeHistorieSeznamDto> => {
	const { data } = await axiosInstance.get('/api/v1/komunikacniudaje/historie');
	return data;
};

export const fetchUserSettingsQuery = async (
	axiosInstance: AxiosInstance
): Promise<NastaveniSeznamApiActualDto> => {
	const { data } =
		await axiosInstance.get<NastaveniSeznamApiActualDto>('/api/v1/nastaveni');
	return data;
};

// As of writing this, the API does not support saving more than one setting at a time.
export const saveUserSettingQuery = (
	axiosInstance: AxiosInstance,
	setting: UserSetting
): Promise<AxiosResponse<void, void>> =>
	axiosInstance.post(
		'/api/v1/nastaveni',
		{
			klic: setting.key,
			hodnota: setting.value,
		},
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

export const saveFirebaseTokenQuery = ({
	payload,
	axiosInstance,
}: {
	axiosInstance: AxiosInstance;
	payload: FirebaseTokenPayload;
}): Promise<AxiosResponse<void, void>> =>
	axiosInstance.post(
		'/api/v1/be/nastaveni/fbtoken',
		{
			klic: payload.key,
			hodnota: payload.value,
		},
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

export const fetchFirebaseTokenQuery = async (
	axiosInstance: AxiosInstance,
	token: string
): Promise<Nullable<string>> => {
	const key = composeKeyFromFirebaseToken(token);
	if (key === null) return null;

	try {
		const { data } = await axiosInstance.get<NastaveniSeznamApiActualDto>(
			`/api/v1/be/nastaveni/fbtoken/${key}`
		);
		if (data.seznam.length < 1) return null;
		return (data.seznam[0]?.hodnota ?? null) as Nullable<string>;
	} catch (e) {
		/* Silent suppress error */
	}

	return null;
};

export const deleteFirebaseTokenQuery = ({
	key,
	axiosInstance,
}: {
	axiosInstance: AxiosInstance;
	key: Nullable<string>;
}): Promise<AxiosResponse<void, void>> =>
	axiosInstance.delete(`/api/v1/be/nastaveni/fbtoken/${key}`);

export const composeKeyFromFirebaseToken = (
	token: string
): Nullable<string> => {
	if ((token?.indexOf(':') ?? -1) === -1) {
		return null;
	}
	return `N_FBTOKEN_${token?.split(':')[0]}`;
};
